/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { InfoRounded } from '@mui/icons-material';
import * as R from 'ramda';

// Utils
import { useUserRolePermissions } from 'hooks';

// Components
import { ReactComponent as MagellanLogo } from 'assets/img/magellan-logo-grey.svg';
import SubscriptionCard from '../components/subscription-card/SubscriptionCard';
/* Hide Swift Enrollment Process from Swift Production
import CurrentSubscription from '../current-subscription/CurrentSubscription.connector'; */

// Context
import { SubscriptionsContext } from '../SubscriptionsContext';

const headerWrapper = theme => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  paddingBottom: theme.spacing(3),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '130px 1fr 130px',
  },
});

const infoIcon = {
  height: 18,
  width: 18,
};

const rulesButtonStyles = theme => ({
  alignItems: 'center',
  color: theme.palette.primary.main,
  display: 'flex',
  gridColumnStart: 'unset',
  justifyContent: 'start',
  marginBottom: theme.spacing(2),
  textDecoration: 'none',
  [theme.breakpoints.up('md')]: {
    gridColumnStart: 3,
    marginBottom: 'unset',
  },
  width: 130,
});

const SubscriptionEnrollment = props => {
  const {
    currentSubscriptions,
    currentSubscriptionsLoading,
    fetchCurrentSubscriptions,
    planChosen,
    resetSubscriptions,
    setPlanChosen,
  } = props;
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'), { noSsr: true });

  const { isAdmin } = useUserRolePermissions();

  const { initialData, setCurrentStep, setData } = useContext(SubscriptionsContext);

  const styles = {
    body: {
      fontSize: '18px',
    },
    container: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      justifyContent: 'center',
    },
    header: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
  };

  useEffect(() => {
    setCurrentStep(0);
    if (!R.isEmpty(planChosen)) {
      setData(initialData);
      resetSubscriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      fetchCurrentSubscriptions();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleClick = () => {
    setPlanChosen('volume');
    setCurrentStep(1);
    navigate(`${pathname}/step1`);
  };

  const isLoading = currentSubscriptionsLoading || R.isNil(currentSubscriptions);

  const hasSubscriptions = currentSubscriptions?.length > 0;
  const forceEnrollmentFirstBranch = state?.fromSelfAuthGridNonSwift === false;

  return forceEnrollmentFirstBranch || isLoading || hasSubscriptions ? (
    /* Hide Swift Enrollment Process from Swift Production
    <CurrentSubscription isLoading={isLoading} /> */
    <Box sx={styles.container}>
      <Typography sx={styles.header} variant="h4">
        Contact for Swift+ Enrollments
      </Typography>
      <Typography sx={styles.body} variant="body1">
        commercialtechnology@oneok.com
      </Typography>
    </Box>
  ) : (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      height="calc(100% - 112px)"
      justifyContent="space-between"
      marginTop={6}
      overflow="auto"
      paddingTop={3}
      position="absolute"
      px={3}
      width="100%"
    >
      <Box
        alignItems={mdUp ? 'center' : 'flex-start'}
        display="flex"
        flexDirection="column"
        width="100%"
      >
        <Box sx={headerWrapper}>
          {/* TODO: Update to prop when pdf becomes available */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Box
            component={Link}
            sx={rulesButtonStyles}
            target="_blank"
            to="/downloads/ProgramRules_SwiftPlus_08262021.pdf"
          >
            <Typography
              sx={{
                color: 'primary.main',
                paddingRight: 1,
              }}
              variant="body2"
            >
              Terms
            </Typography>
            <InfoRounded sx={infoIcon} />
          </Box>
          <Typography
            sx={{
              gridColumnStart: { xs: 'unset', md: 2 },
              gridRowStart: { xs: 'unset', md: 1 },
              textAlign: { xs: 'left', md: 'center' },
            }}
            variant="h5"
          >
            Enroll in Swift+
          </Typography>
        </Box>
        <Typography
          sx={{
            margin: '0 auto',
            mb: 3,
            maxWidth: 548,
            textAlign: { xs: 'left', md: 'center' },
          }}
          variant="body1"
        >
          Access enhanced functionalities and lower effective rates by enrolling in Swift+. See
          Terms for more details.
        </Typography>
        <Box sx={{ margin: '0 auto' }}>
          <SubscriptionCard
            hasSubscriberPermissions={isAdmin}
            noPermissionButtonTooltipText="You do not have permissions to enroll in Swift+.
          Please contact your company administrator."
            onClick={() => handleClick()}
            routePath="step1"
            title="Annual Volume Tier"
          />
        </Box>
      </Box>
      {mdUp && (
        <Box alignItems="center" display="flex" height="41" paddingBottom={3} width={291}>
          <MagellanLogo />
        </Box>
      )}
    </Box>
  );
};

SubscriptionEnrollment.propTypes = {
  currentSubscriptions: arrayOf(shape({})).isRequired,
  currentSubscriptionsLoading: bool.isRequired,
  fetchCurrentSubscriptions: func.isRequired,
  planChosen: string,
  resetSubscriptions: func.isRequired,
  setPlanChosen: func.isRequired,
};

SubscriptionEnrollment.defaultProps = {
  planChosen: '',
};
export default SubscriptionEnrollment;
